import React, { useState, useEffect, useRef, useCallback } from "react";
import EmojiPicker from "emoji-picker-react";
import "./testChat.css";

import { useConnection } from "../../context/SoulmateProvider";
import { useSocket } from "../../context/SocketProvider";
import { useLogin } from "../../context/LoginProvider";

export default function TestChat({ roomId, isJoined }) {
  const { similarityPercentage, isTreeBasedMatch, soulmateUsername, soulmateStatus } = useConnection();
  const { socket, audioSocket } = useSocket();
  const { userData, isLoggedIn, guestData } = useLogin();
  const [message, setMessage] = useState(""); // Message input state
  const [messages, setMessages] = useState([]);
  const [isdisabled, setDisabled] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSimilarityBanner, setShowSimilarityBanner] = useState(false);
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Add new state for typing status
  const [isTyping, setIsTyping] = useState(false);
  const [strangerTyping, setStrangerTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  //use effect to scroll to bottom when new message arrives
  useEffect(() => {
    scrollToBottom();
  }, [messages, suggestions]);

  //useEffect to update interests
  useEffect(() => {
    setDisabled(!isJoined);

    if (messages.length !== 0) {
      audioSocket.emit("update-interests", {
        username: isLoggedIn ? userData.username : guestData.username,
        messages: messages,
        isLoggedIn: isLoggedIn,
      });
    }
    setSuggestions([]);
    setMessages([]);
  }, [isJoined]);

  //useEffect to update suggestions

  // useEffect(() => {
  //   if (!isJoined || messages.length === 0) return;
  //   audioSocket.emit("updateSuggestions", {
  //     soulmateUsername,
  //     messages,
  //     soulmateStatus,
  //   });
  // }, [messages]);

  // Array to store received messages
  const handleMessage = (data) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { isUser: false, isSol: false, message: data.message },
    ]);
  };

  const handleStarters = useCallback(
    (data) => {
      if (messages.length === 0) {
        setMessages([
          { isUser: false, isSol: true, message: data.starters[0] },
        ]);
      }
      setSuggestions(data.starters);
    },
    [messages]
  );

  // Add debounced typing function
  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      socket.emit("typing", { roomId });
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      socket.emit("stopTyping", { roomId });
    }, 1000);
  };

  //UseEffect for sockets
  useEffect(() => {
    socket.on("message", handleMessage);
    socket.on("starters", handleStarters);
    socket.on("userTyping", () => {
      setStrangerTyping(true);
    });

    socket.on("userStoppedTyping", () => {
      setStrangerTyping(false);
    });

    return () => {
      socket.off("message", handleMessage);
      socket.off("starters", handleStarters);
      socket.off("userTyping");
      socket.off("userStoppedTyping");
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [socket]);
  useEffect(() => {
    audioSocket.on("starters", handleStarters);

    return () => {
      audioSocket.off("starters", handleStarters);
    };
  }, [audioSocket, messages]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const sendMessage = (event) => {
    event.preventDefault();
    if (!message.trim()) return;

    socket.emit("message", { roomId, message });
    setMessages((prevMessages) => [
      ...prevMessages,
      { isUser: true, isSol: false, message: message.trim() },
    ]);
    setMessage("");
  };

  function handleClickOnStarter({ suggestion, index }) {
    socket.emit("message", { roomId, message: suggestion });
    setMessages((prevMessages) => [
      ...prevMessages,
      { isUser: true, message: suggestion },
    ]);
    var newSuggestions = suggestions;
    newSuggestions.splice(index, 1);
    setSuggestions(newSuggestions);
  }

  const handleKeyDown = (event) => {
    if (message === "") return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage(event);
    }
  };

  const onEmojiClick = (emojiData) => {
    setMessage((prevMessage) => prevMessage + emojiData.emoji);
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    if (isJoined && e.target.value) {
      handleTyping();
    }
  };

  // Effect to handle similarity banner visibility
  useEffect(() => {
    if (isJoined) {
      setShowSimilarityBanner(true);
      const timer = setTimeout(() => {
        setShowSimilarityBanner(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isJoined]);

  return (
    <div className="chat-container">
      {isJoined && showSimilarityBanner && (
        <div className="similarity-banner">
          <span>
            {isTreeBasedMatch
              ? `You have ${similarityPercentage}% interests in common!`
              : "Randomly matched! Let's discover common interests!"}
          </span>
        </div>
      )}
      <div className="chat-header">
        <div className="chat-header-title">
          <span>Chatroom</span>
          {isJoined && soulmateUsername && (
            <span className="remote-username">
              [{soulmateStatus ? soulmateUsername : `Guest ${soulmateUsername}`}]
            </span>
          )}
        </div>
        {isJoined ? (
          <div className="connectionStatusContainerLite">
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="16" height="16" rx="8" fill="#4CAF50" />
            </svg>
            <span className="connectionStatusTextLite">Connected</span>
          </div>
        ) : (
          <div className="connectionStatusContainerLite">
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="16" height="16" rx="8" fill="#FFC107" />
            </svg>
            <span className="connectionStatusTextLite">Not Connected</span>
          </div>
        )}
      </div>
      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={msg.isUser ? "rightAttached" : "leftAttached"}
          >
            {msg.isUser ? "You :- " : msg.isSol ? "Sol :- " : "Stranger :- "}{" "}
            {msg.message}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input-container">
        {strangerTyping && (
          <div className="typing-indicator">
            <span></span>
            <div className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        <div className="emoji-picker-container" ref={emojiPickerRef}>
          <button
            type="button"
            className="emoji-button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            😊
          </button>
          {showEmojiPicker && (
            <div className="emoji-picker-wrapper">
              <EmojiPicker onEmojiClick={onEmojiClick} />
            </div>
          )}
        </div>
        <input
          type="text"
          value={message}
          onChange={handleInputChange}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage(e)}
          placeholder={isJoined ? "Type a message..." : "Waiting for connection..."}
          className="chat-input"
          disabled={!isJoined}
        />
        <button
          className="send-button"
          onClick={sendMessage}
          disabled={!isJoined || !message.trim()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}